/**
 * NutriPeak — Mobile Sticky Add-to-Cart
 * --------------------------------------
 * Presentation-only bar. It does NOT own a product form. It reuses the theme's
 * main product form so the add-to-cart, side-cart refresh and drawer open all
 * run through the theme's own native code path (zero stale-variant risk).
 *
 *  - Variant state  : `product-variant-updated` document event + main [name="id"]
 *  - Drive variant  : toggle the real `.product-variants .field-value` controls
 *  - Add to cart     : proxy-click the real `.add-to-cart-button`
 *  - Open drawer     : handled by the theme (html.side-cart-open)
 */
(function () {
  'use strict';

  // teardown registry — global observers/listeners are torn down before re-init
  // (Theme Editor re-renders the section), preventing leaks across reloads.
  var teardowns = [];
  function cleanup() {
    teardowns.forEach(function (fn) { try { fn(); } catch (e) {} });
    teardowns = [];
  }

  function init(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var bars = root.querySelectorAll('[data-np-sticky-atc]');
    for (var i = 0; i < bars.length; i++) setup(bars[i]);
  }

  function setup(bar) {
    if (bar.dataset.npReady === '1') return;

    // --- locate the theme's main product form (single source of truth) ---
    var summary = document.querySelector('.main-product-section .product-summary') || document.querySelector('.product-summary');
    var mainForm = summary && summary.querySelector('form[data-type="add-to-cart-form"]');
    var mainAddBtn = mainForm && mainForm.querySelector('button[name="add"].add-to-cart-button');
    var mainIdInput = mainForm && mainForm.querySelector('input[name="id"]');
    if (!summary || !mainForm || !mainAddBtn) return; // not a ready PDP

    cleanup();               // remove any previous instance's global hooks
    bar.dataset.npReady = '1';

    var variantsRoot = document.querySelector('.product-variants');
    var variantData = parseVariants();

    var hasVariants = bar.hasAttribute('data-has-variants') && !!variantsRoot;
    var labelAdd = bar.getAttribute('data-label-add') || 'Add to cart';
    var labelSold = bar.getAttribute('data-label-soldout') || 'Sold out';

    // --- bar elements ---
    var addBtn = bar.querySelector('[data-np-add]');
    var chip = bar.querySelector('[data-np-open]');
    var chipText = bar.querySelector('[data-np-variant-text]');
    var barThumbs = bar.querySelectorAll('[data-np-thumb]');
    var mirrorPriceEls = bar.querySelectorAll('[data-np-price]'); // sheet price (mirrors theme)
    var nowEl = bar.querySelector('[data-np-now]');                // bar current price
    var wasEl = bar.querySelector('[data-np-was]');                // bar compare price
    var labelEls = bar.querySelectorAll('[data-np-add-label]');

    // --- sheet elements ---
    var sheet = bar.querySelector('[data-np-sheet]');
    var scrim = bar.querySelector('[data-np-scrim]');
    var sheetAddBtn = bar.querySelector('[data-np-sheet-add]');
    var closeBtn = bar.querySelector('[data-np-close]');
    var groups = [].slice.call(bar.querySelectorAll('[data-np-group]'));
    var optionNames = groups.map(function (g) { return g.getAttribute('data-np-group'); });

    // --- custom pack selector (snippets/np-pack-selector.liquid) integration ---
    // Pack/size options are rendered on the page by the .np-packs selector (radios,
    // each `disabled` when sold out). The theme picker only emits a hidden
    // data-pack-skip proxy for them — so for pack options we mirror availability
    // from, and drive selection through, the page's OWN pack selector.
    var packsRoot = document.querySelector('[data-np-packs]');
    function packRadioFor(value) {
      if (!packsRoot) return null;
      var rs = packsRoot.querySelectorAll('.np-packs__radio');
      for (var i = 0; i < rs.length; i++) { if (rs[i].value === value) return rs[i]; }
      return null;
    }
    // which bar group (if any) is the pack option
    var packGroupIndex = -1;
    if (packsRoot) {
      groups.forEach(function (g, gi) {
        var o = g.querySelector('[data-np-opt]');
        if (o && packRadioFor(o.getAttribute('data-value'))) packGroupIndex = gi;
      });
    }

    /* ---------------- variant helpers ---------------- */
    function parseVariants() {
      // MUST scope to THIS product's summary: many other sections (testimonial/
      // shopable-video/scrolling-promotion carousels, side-cart, product cards) also
      // emit [data-product-variants], and a global lookup can grab a DIFFERENT
      // product's data — which makes every option look sold out.
      var el = (summary && summary.querySelector('[data-product-variants]')) || document.querySelector('[data-product-variants]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    function readSelection() {
      var sel = [];
      optionNames.forEach(function (name, i) {
        var val = null;
        if (i === packGroupIndex && packsRoot) {
          // pack option: the live value is the checked pack-selector radio
          var pr = packsRoot.querySelector('.np-packs__radio:checked');
          if (pr) val = pr.value;
        } else {
          variantsRoot.querySelectorAll('.field-value').forEach(function (c) {
            if (c.hasAttribute('data-pack-skip')) return; // ignore pack-selector proxies
            if (c.type === 'radio') { if (c.name === name && c.checked) val = c.value; }
            else if (c.tagName === 'SELECT') { if (c.name === '[' + name + ']') val = c.value; }
          });
        }
        sel[i] = val;
      });
      return sel;
    }

    // a value is available if some available variant has it AND matches the
    // currently-chosen values of the OTHER options (Shopify grey-out logic)
    function valueAvailable(optIndex, value, sel) {
      if (!variantData) return true;
      return variantData.some(function (v) {
        if (!v.available) return false;
        if (v.options[optIndex] !== value) return false;
        for (var i = 0; i < optionNames.length; i++) {
          if (i === optIndex) continue;
          if (sel[i] != null && v.options[i] !== sel[i]) return false;
        }
        return true;
      });
    }

    // availability that mirrors the PAGE exactly: pack options follow the pack
    // selector's own disabled state; everything else uses the variant-data logic.
    function valueIsAvailable(optIndex, value, sel) {
      var pr = packRadioFor(value);
      if (pr) return !pr.disabled;            // pack option → mirror the page's pack selector
      if (!variantData) return true;
      // Safety net: if this value isn't present in the variant data at all, the data
      // belongs to a DIFFERENT product (a PDP carries several [data-product-variants]
      // scripts) — never falsely strike out a real option.
      var known = variantData.some(function (v) { return v.options && v.options[optIndex] === value; });
      if (!known) return true;
      return valueAvailable(optIndex, value, sel);
    }

    // drive the real control so id/price/availability update (same path the page uses)
    function chooseOption(name, value) {
      // pack/size option → drive the page's own pack selector; its script syncs the
      // theme picker, the variant id and the price for us.
      var pr = packRadioFor(value);
      if (pr) {
        if (!pr.disabled && !pr.checked) { pr.checked = true; pr.dispatchEvent(new Event('change', { bubbles: true })); }
        return true;
      }
      var done = false;
      variantsRoot.querySelectorAll('.field-value').forEach(function (c) {
        if (done || c.hasAttribute('data-pack-skip')) return;
        if (c.type === 'radio' && c.name === name && c.value === value) {
          c.checked = true; c.dispatchEvent(new Event('change', { bubbles: true })); done = true;
        } else if (c.tagName === 'SELECT' && c.name === '[' + name + ']') {
          c.value = value; c.dispatchEvent(new Event('change', { bubbles: true })); done = true;
        }
      });
      return done;
    }

    // availability is on the FORM (.product-buy.disabled) for sold-out default
    // variants, and on the BUTTON for picker-driven sold-out combos. Check both.
    function isUnavailable() {
      return mainAddBtn.classList.contains('disabled') || mainForm.classList.contains('disabled');
    }

    // resolve the currently selected variant object (for price)
    function currentVariant() {
      if (!variantData) return null;
      var id = mainIdInput && parseInt(mainIdInput.value, 10);
      if (id) {
        for (var i = 0; i < variantData.length; i++) if (variantData[i].id === id) return variantData[i];
      }
      var sel = readSelection();
      return variantData.filter(function (v) {
        return v.options.every(function (o, i) { return sel[i] == null || o === sel[i]; });
      })[0] || null;
    }

    // format cents using the shop's money_format (e.g. "Rs. {{amount}}")
    function formatMoney(cents) {
      var format = (window.btyGlobals && window.btyGlobals.money_format) || '{{amount}}';
      function fwd(num, precision, thousands, decimal) {
        thousands = thousands == null ? ',' : thousands; decimal = decimal == null ? '.' : decimal;
        if (isNaN(num) || num == null) return '0';
        var parts = (num / 100).toFixed(precision).split('.');
        var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        return whole + (parts[1] ? decimal + parts[1] : '');
      }
      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, name) {
        switch (name) {
          case 'amount_no_decimals': return fwd(cents, 0);
          case 'amount_with_comma_separator': return fwd(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fwd(cents, 0, '.', ',');
          case 'amount_with_space_separator': return fwd(cents, 2, ' ', '.');
          case 'amount_with_apostrophe_separator': return fwd(cents, 2, "'", '.');
          default: return fwd(cents, 2);
        }
      });
    }

    // Read the theme's OWN price node — the single source of truth. It already
    // reflects sale state, subscriptions / selling-plans (Appstle), pack/bundle
    // pricing and currency, so the bar can never drift from the main buy box.
    function readThemePrice() {
      var node = summary.querySelector('.product-price');
      if (!node) return null;
      var clone = node.cloneNode(true);
      clone.querySelectorAll('.sr-only,.visually-hidden,.visually-hidden--inline,.product-sale-label').forEach(function (n) { n.remove(); });
      var t = function (sel) { var e = clone.querySelector(sel); return e ? e.textContent.replace(/\s+/g, ' ').trim() : ''; };
      var onSale = node.classList.contains('price--on-sale') || !!clone.querySelector('.price__sale .price, .price-item--sale');
      if (onSale) {
        return {
          now: t('.price__sale .price') || t('.price-item--sale') || t('.price-item--regular'),
          was: t('.price__sale s') || t('s.regular-price') || t('.price-item--last')
        };
      }
      return { now: t('.price-item--regular') || t('.price-item') || t('.price'), was: '' };
    }

    function updateBarPrice() {
      if (!nowEl) return;
      var tp = readThemePrice();
      if (tp && tp.now) {                       // mirror the theme exactly (correct for subs/packs/sales)
        nowEl.textContent = tp.now;
        if (wasEl) {
          if (tp.was && tp.was !== tp.now) { wasEl.textContent = tp.was; wasEl.hidden = false; }
          else { wasEl.hidden = true; }
        }
        return;
      }
      // fallback: compute from the selected variant's cents if the theme node is missing
      var cv = currentVariant();
      if (!cv) return;
      nowEl.textContent = formatMoney(cv.price);
      if (wasEl) {
        if (cv.compare_at_price && cv.compare_at_price > cv.price) {
          wasEl.textContent = formatMoney(cv.compare_at_price); wasEl.hidden = false;
        } else { wasEl.hidden = true; }
      }
    }

    /* ---------------- reflect theme state into the bar ---------------- */
    function syncFromTheme() {
      // bar price — own markup (current on top, discount wraps below)
      updateBarPrice();

      // sheet price — mirror the theme's own price node (identical formatting + sale state).
      var mainPrice = summary.querySelector('.product-price');
      if (mainPrice && mirrorPriceEls.length) {
        var html = mainPrice.outerHTML;
        mirrorPriceEls.forEach(function (p) { p.innerHTML = html; });
      }

      // availability from the theme (form OR button); label stays clean.
      var disabled = isUnavailable();
      labelEls.forEach(function (el) { el.textContent = disabled ? labelSold : labelAdd; });
      bar.classList.toggle('is-unavailable', disabled);

      if (!hasVariants) return;

      var sel = readSelection();
      if (chipText) chipText.textContent = sel.filter(Boolean).join(' · ');

      groups.forEach(function (g, gi) {
        var valEl = g.querySelector('[data-np-group-val]');
        if (valEl) valEl.textContent = sel[gi] || '';
        g.querySelectorAll('[data-np-opt]').forEach(function (opt) {
          var v = opt.getAttribute('data-value');
          opt.classList.toggle('is-on', sel[gi] === v);
          opt.classList.toggle('is-soldout', !valueIsAvailable(gi, v, sel));
        });
      });
    }

    function updateThumb(src) {
      if (!src) return;
      var sized = src.split('?')[0] + '?width=200';
      barThumbs.forEach(function (img) { if (img.tagName === 'IMG') img.src = sized; });
    }

    /* ---------------- sheet open / close + focus management ---------------- */
    var lastFocus = null;

    function focusables() {
      if (!sheet) return [];
      var nodes = sheet.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      return [].filter.call(nodes, function (el) { return !el.disabled && el.offsetParent !== null; });
    }
    function onKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) { closeSheet(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    function openSheet() {
      if (!hasVariants || !sheet) return;
      lastFocus = document.activeElement;
      bar.classList.add('np-sheet-open');
      document.documentElement.classList.add('np-satc-lock');
      document.addEventListener('keydown', onKeydown);
      setTimeout(function () { (closeBtn || sheet).focus(); }, 60);
    }
    function closeSheet() {
      if (!bar.classList.contains('np-sheet-open')) {
        document.documentElement.classList.remove('np-satc-lock');
        return;
      }
      bar.classList.remove('np-sheet-open');
      document.documentElement.classList.remove('np-satc-lock');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
      lastFocus = null;
    }

    /* ---------------- add to cart (proxy-click theme button) ---------------- */
    var busy = false, safety = null;

    function setBusy() {
      busy = true;
      toggle([addBtn, sheetAddBtn], 'is-loading', true);
      clearTimeout(safety);
      safety = setTimeout(resetBusy, 6000); // network safety net
    }
    function setSuccess() {
      if (!busy) return;
      busy = false; clearTimeout(safety);
      closeSheet();                                   // get out of the drawer's way immediately
      toggle([addBtn, sheetAddBtn], 'is-loading', false);
      toggle([addBtn, sheetAddBtn], 'is-added', true);
      setTimeout(function () { toggle([addBtn, sheetAddBtn], 'is-added', false); }, 1800);
    }
    function resetBusy() {
      busy = false; clearTimeout(safety);
      toggle([addBtn, sheetAddBtn], 'is-loading', false);
      toggle([addBtn, sheetAddBtn], 'is-added', false);
      closeSheet();                                   // failure: don't strand the user in a locked sheet
    }
    function toggle(els, cls, on) {
      els.forEach(function (el) { if (el) el.classList.toggle(cls, on); });
    }

    function doAdd() {
      if (busy || isUnavailable()) return;
      setBusy();
      mainAddBtn.click(); // theme handles fetch + drawer
    }

    // success signal #1 — theme opened the side cart (class flips to present)
    var htmlObs = new MutationObserver(function () {
      if (busy && document.documentElement.classList.contains('side-cart-open')) setSuccess();
    });
    htmlObs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    teardowns.push(function () { htmlObs.disconnect(); });

    // success signal #2 — cart count changed (covers an already-open drawer)
    var cartCountEl = document.querySelector('.cart-item-count');
    if (cartCountEl) {
      var ccObs = new MutationObserver(function () { if (busy) setSuccess(); });
      ccObs.observe(cartCountEl, { childList: true, characterData: true, subtree: true });
      teardowns.push(function () { ccObs.disconnect(); });
    }

    // error fallback — theme finished loading but no drawer / no count change
    var btnObs = new MutationObserver(function () {
      if (busy && !mainAddBtn.classList.contains('loading')) {
        setTimeout(function () {
          if (busy && !document.documentElement.classList.contains('side-cart-open')) resetBusy();
        }, 500);
      }
    });
    btnObs.observe(mainAddBtn, { attributes: true, attributeFilter: ['class'] });
    teardowns.push(function () { btnObs.disconnect(); });

    /* ---------------- wire events ---------------- */
    if (addBtn) addBtn.addEventListener('click', function () {
      if (hasVariants) openSheet(); else doAdd();   // variants → confirm in sheet first
    });
    if (sheetAddBtn) sheetAddBtn.addEventListener('click', doAdd);
    if (chip) chip.addEventListener('click', openSheet);
    if (scrim) scrim.addEventListener('click', closeSheet);
    if (closeBtn) closeBtn.addEventListener('click', closeSheet);

    groups.forEach(function (g) {
      g.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-np-opt]');
        if (!opt || opt.classList.contains('is-soldout')) return;
        chooseOption(opt.getAttribute('data-option-name'), opt.getAttribute('data-value'));
      });
    });

    // theme variant broadcast (fires for valid combos, incl. on init)
    function onVariantUpdated(e) {
      if (e && e.detail && e.detail.selected && e.detail.selected.featured_image) {
        updateThumb(e.detail.selected.featured_image.src || e.detail.selected.featured_image);
      }
      syncFromTheme();
    }
    document.addEventListener('product-variant-updated', onVariantUpdated);
    teardowns.push(function () { document.removeEventListener('product-variant-updated', onVariantUpdated); });

    // low-level fallback in BUBBLE phase + deferred, so it reads state AFTER the
    // theme's target-phase onchange has mutated the button (covers no-event combos)
    if (variantsRoot) {
      var onFieldChange = function (e) {
        if (e.target && e.target.classList && e.target.classList.contains('field-value')) {
          setTimeout(syncFromTheme, 0);
        }
      };
      variantsRoot.addEventListener('change', onFieldChange);
      teardowns.push(function () { variantsRoot.removeEventListener('change', onFieldChange); });
    }

    // Subscription / pack widgets (Appstle, selling-plans) can change the displayed
    // price WITHOUT firing a variant event — watch the theme price node and re-sync
    // the bar whenever the shown price text actually changes.
    var lastPriceText = (function () { var n = summary.querySelector('.product-price'); return n ? n.textContent.replace(/\s+/g, ' ').trim() : ''; })();
    if ('MutationObserver' in window) {
      var priceObserver = new MutationObserver(function () {
        var n = summary.querySelector('.product-price');
        var snap = n ? n.textContent.replace(/\s+/g, ' ').trim() : '';
        if (snap !== lastPriceText) { lastPriceText = snap; syncFromTheme(); }
      });
      try { priceObserver.observe(summary, { childList: true, subtree: true, characterData: true }); } catch (e) {}
      teardowns.push(function () { priceObserver.disconnect(); });
    }

    /* ---------------- reveal on scroll ---------------- */
    // body flag mirrors the bar's true on-screen state (used to lift floating widgets)
    function refreshVisible() {
      var vis = bar.classList.contains('is-visible') && !bar.classList.contains('np-at-footer');
      document.body.classList.toggle('np-atc-visible', vis);
      liftFloat(vis && window.matchMedia('(max-width: 991px)').matches);
    }
    teardowns.push(function () { document.body.classList.remove('np-atc-visible'); });

    /* lift the stacked floating buttons (WhatsApp + back-to-top arrow + any chat widget)
       above the bar. We lift them ALL by the same amount so they keep their spacing and
       never collide with each other — even on short screens. */
    var floatWidgets = [], floatTries = 0;
    function fixedAncestor(node) {
      while (node && node !== document.body) {
        var pos = ''; try { pos = getComputedStyle(node).position; } catch (e) {}
        if (pos === 'fixed') return node;
        node = node.parentElement;
      }
      return null;
    }
    function collectFloats() {
      floatWidgets = floatWidgets.filter(function (el) { return document.contains(el); }); // drop detached
      if (floatTries > 40) return floatWidgets;     // stop scanning after enough attempts
      floatTries++;
      var found = floatWidgets.slice();             // accumulate — keep ones found earlier (e.g. before the app embed loaded)
      function add(el) { if (el && found.indexOf(el) === -1) found.push(el); }
      // (a) WhatsApp by link/class/id → its fixed container
      var kw = document.querySelectorAll('a[href*="wa.me"],a[href*="whatsapp" i],[class*="whatsapp" i],[id*="whatsapp" i],iframe[src*="whatsapp" i]');
      for (var i = 0; i < kw.length; i++) { var f = fixedAncestor(kw[i]); if (f) add(f); }
      // (b) every small fixed button stacked along the bottom-right (WhatsApp, back-to-top, chat…)
      var vw = window.innerWidth, vh = window.innerHeight;
      var cands = document.querySelectorAll('div,a,button,aside');
      for (var j = 0; j < cands.length; j++) {
        var el = cands[j];
        if (el.closest('[data-np-sticky-atc]') || el.closest('[data-np-pvideo]')) continue; // skip our widgets
        var cs; try { cs = getComputedStyle(el); } catch (e) { continue; }
        if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
        var r = el.getBoundingClientRect();
        if (r.width < 24 || r.height < 24 || r.width > 150 || r.height > 150) continue;        // button-sized
        if ((vw - r.right) > 60 || (vh - r.bottom) > 260) continue;                            // right edge, lower zone (covers the arrow above WhatsApp)
        add(el);
      }
      // keep only the OUTERMOST element of each widget (so we never transform a button and its container twice)
      floatWidgets = found.filter(function (el) { return !found.some(function (o) { return o !== el && o.contains(el); }); });
      return floatWidgets;
    }
    function liftFloat(on) {
      var list = collectFloats(); if (!list.length) return;
      var amt = 0;
      if (on) { var barEl = bar.querySelector('.np-satc__bar'); amt = (barEl ? barEl.offsetHeight : 68) + 12; }
      list.forEach(function (w) {
        w.style.transition = 'transform .3s ease';
        w.style.transform = on ? ('translateY(-' + amt + 'px)') : '';
      });
    }
    teardowns.push(function () { floatWidgets.forEach(function (w) { w.style.transform = ''; }); });
    // the app embed can inject late — re-evaluate several times after load so it gets picked up
    [600, 1500, 3000, 5000].forEach(function (d) { setTimeout(refreshVisible, d); });

    if (bar.getAttribute('data-reveal') === 'scroll' && 'IntersectionObserver' in window) {
      var seen = false;
      var revealObs = new IntersectionObserver(function (entries) {
        var en = entries[0];
        if (en.isIntersecting) seen = true;            // only reveal AFTER the main ATC has been seen
        bar.classList.toggle('is-visible', seen && en.intersectionRatio <= 0);
        refreshVisible();
      }, { threshold: 0 });
      revealObs.observe(mainAddBtn);
      teardowns.push(function () { revealObs.disconnect(); });
    } else {
      bar.classList.add('is-visible');
      refreshVisible();
    }

    // don't cover the footer / last section at the very bottom of the page
    var footer = document.querySelector('.footer, footer, [id*="section-footer"], .site-footer');
    if (footer && 'IntersectionObserver' in window) {
      var footObs = new IntersectionObserver(function (entries) {
        bar.classList.toggle('np-at-footer', entries[0].isIntersecting);
        refreshVisible();
      }, { threshold: 0 });
      footObs.observe(footer);
      teardowns.push(function () { footObs.disconnect(); });
    }

    syncFromTheme();
  }

  /* ---------------- boot ---------------- */
  if (document.readyState !== 'loading') init(document);
  else document.addEventListener('DOMContentLoaded', function () { init(document); });

  // Theme editor: re-init when the section is re-rendered
  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target.querySelector ? e.target : document;
    var b = t.querySelector ? t.querySelector('[data-np-sticky-atc]') : null;
    if (b) b.dataset.npReady = '';
    init(t);
  });
})();
